import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Loader2, X, Download, FileText, Check, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VARIANTS = {
  'connectivity-map': {
    endpoint: '/api/lead-magnet/connectivity-map',
    filename: 'Rahatani-Connectivity-Map-Nova-Homes.pdf',
    kicker: 'Free Download · PDF',
    title: 'Rahatani Connectivity Map',
    subtitle:
      "A field-verified, single-page reference of every landmark you'll actually use — DMart, BRTS, Sant Tukaram Nagar Metro, Hinjewadi, schools, hospitals — with exact distances and drive-times.",
    cta: 'Download Connectivity Map',
    successText: 'Connectivity Map sent. Want the price list too?',
    crossOffer: 'brochure',
  },
  brochure: {
    endpoint: '/api/lead-magnet/brochure',
    filename: 'Nova-Homes-Project-Brochure-Price-List.pdf',
    kicker: 'Free Download · PDF',
    title: 'Full Project Brochure & Price List',
    subtitle:
      'All three Nova Homes projects in one PDF — configurations, carpet areas, indicative pricing, amenities, and RERA numbers. Delivered instantly.',
    cta: 'Download Brochure & Price List',
    successText: 'Brochure sent. Want the connectivity map too?',
    crossOffer: 'connectivity-map',
  },
  'floor-plans': {
    // endpoint computed below — needs project slug
    filenameFor: (slug) => `${(slug || 'project').replace(/-/g, '_')}_floor_plans_nova_homes.pdf`,
    kicker: 'Free Download · PDF',
    title: 'Floor Plans — Detailed Layout PDF',
    subtitle:
      'A multi-page PDF with every floor plan for this project — unit layouts, typical floor plans, carpet areas, and RERA — embedded at print-quality and ready to share.',
    cta: 'Download Floor Plans PDF',
    successText: 'Floor Plans sent. Want the full price list too?',
    crossOffer: 'brochure',
  },
};

// Cross-offer copy (the "you might also want" prompt)
const CROSS_OFFER_COPY = {
  'connectivity-map': {
    kicker: 'One More Free PDF',
    title: 'Want the field-verified Connectivity Map too?',
    subtitle:
      'Every landmark — DMart, BRTS, Sant Tukaram Nagar Metro, Hinjewadi, schools, hospitals — with exact distances and drive-times. We\'ll send it to the same number.',
    cta: 'Yes, send the Connectivity Map',
  },
  brochure: {
    kicker: 'One More Free PDF',
    title: 'Want the full Pricing Brochure too?',
    subtitle:
      'Configurations, carpet areas, indicative pricing, amenities, and RERA for all three Nova Homes projects — one branded PDF.',
    cta: 'Yes, send the Pricing Brochure',
  },
  'floor-plans': {
    kicker: 'One More Free PDF',
    title: 'Want the full Floor Plans PDF too?',
    subtitle:
      'Detailed unit layouts and typical floor plans for this project — embedded at print quality. We\'ll send it to the same number.',
    cta: 'Yes, send the Floor Plans',
  },
};

const LeadMagnetModal = ({ open, onClose, source = 'unspecified', variant = 'connectivity-map', projectSlug = '' }) => {
  const config = VARIANTS[variant] || VARIANTS['connectivity-map'];
  const [phase, setPhase] = useState('form'); // 'form' | 'cross-offer' | 'done'
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Resolve endpoint + filename (handle project-slug-aware floor-plans variant)
  const endpointFor = (vKey) => {
    const v = VARIANTS[vKey];
    if (vKey === 'floor-plans') return `/api/lead-magnet/floor-plans/${projectSlug}`;
    return v.endpoint;
  };
  const filenameFor = (vKey) => {
    const v = VARIANTS[vKey];
    if (typeof v.filenameFor === 'function') return v.filenameFor(projectSlug);
    return v.filename;
  };

  // Reset state every time the modal closes/opens or variant flips
  useEffect(() => {
    if (open) {
      setPhase('form');
      setName('');
      setPhone('');
      setSubmitting(false);
    }
  }, [open, variant]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = original; };
    }
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Helper: trigger any variant download with the current name/phone
  const downloadVariant = async (vKey, sourceTag) => {
    const body = { first_name: name.trim(), phone: phone.trim(), source: sourceTag };
    // For the brochure variant, include projectSlug so backend can serve the official, rebranded PDF
    if (vKey === 'brochure' && projectSlug) body.project_slug = projectSlug;
    const res = await fetch(`${BACKEND_URL}${endpointFor(vKey)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filenameFor(vKey);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || phone.trim().length < 10) {
      toast.error('Please enter your name and a valid 10-digit phone.');
      return;
    }
    setSubmitting(true);
    try {
      await downloadVariant(variant, source);
      // Meta Pixel — Lead event on successful PDF lead-magnet capture
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: config.title, source, variant });
      }
      toast.success(config.successText);
      setPhase('cross-offer');
    } catch (err) {
      console.error(err);
      toast.error('Could not download. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCrossOffer = async () => {
    setSubmitting(true);
    try {
      await downloadVariant(config.crossOffer, `${source}-cross-offer`);
      const otherV = VARIANTS[config.crossOffer];
      // Meta Pixel — secondary Lead event for the cross-offer PDF conversion
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: otherV.title, source: `${source}-cross-offer`, variant: config.crossOffer });
      }
      toast.success(`${otherV.title} sent — both PDFs are now in your downloads.`);
      setPhase('done');
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      console.error(err);
      toast.error('Could not download the second PDF. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    setPhase('done');
    setTimeout(() => onClose(), 200);
  };

  const renderForm = () => (
    <>
      <div className="w-12 h-12 bg-[#0a0a0a] text-[#C5A46D] flex items-center justify-center mb-5">
        <FileText size={20} />
      </div>
      <p className="text-[#B5A17C] text-[10px] uppercase tracking-[0.3em] font-medium mb-2">{config.kicker}</p>
      <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#111] leading-tight mb-3">
        {config.title}
      </h3>
      <p className="text-[#666] text-sm font-light leading-relaxed mb-7">
        {config.subtitle}
      </p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="lead-magnet-name"
          placeholder="Your Name"
          required
          className="w-full px-4 py-3 border border-[#111]/[0.12] focus:border-[#111] outline-none text-[#111] text-sm placeholder:text-[#999] transition-colors"
        />
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
          data-testid="lead-magnet-phone"
          placeholder="10-digit Phone Number"
          required
          minLength={10}
          maxLength={10}
          className="w-full px-4 py-3 border border-[#111]/[0.12] focus:border-[#111] outline-none text-[#111] text-sm placeholder:text-[#999] transition-colors"
        />
        <button
          type="submit"
          disabled={submitting}
          data-testid="lead-magnet-submit"
          className="w-full bg-[#0a0a0a] text-white py-3.5 font-medium text-sm tracking-wide hover:bg-[#1a1a1a] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? (
            <><Loader2 size={16} className="animate-spin" /> Preparing your PDF…</>
          ) : (
            <><Download size={15} /> {config.cta}</>
          )}
        </button>
      </form>
      <p className="text-[10px] text-[#999] text-center mt-4 font-light">
        We respect your privacy. No spam, just the PDF and one polite follow-up call.
      </p>
    </>
  );

  const renderCrossOffer = () => {
    const offer = CROSS_OFFER_COPY[config.crossOffer];
    return (
      <div data-testid="lead-magnet-cross-offer" className="animate-in fade-in slide-in-from-bottom-2 duration-400">
        {/* Success header */}
        <div className="flex items-center gap-3 mb-6 pb-5 border-b border-[#111]/[0.06]">
          <div className="w-10 h-10 rounded-full bg-[#10A37F]/10 text-[#10A37F] flex items-center justify-center flex-shrink-0">
            <Check size={18} strokeWidth={2.5} />
          </div>
          <div>
            <p className="text-[#10A37F] text-[10px] uppercase tracking-[0.2em] font-semibold">Downloaded</p>
            <p className="text-[#111] text-sm font-medium">{config.title}</p>
          </div>
        </div>

        {/* Cross-offer card */}
        <div className="w-12 h-12 bg-[#C5A46D] text-[#0a0a0a] flex items-center justify-center mb-5">
          <Sparkles size={20} strokeWidth={2} />
        </div>
        <p className="text-[#B5A17C] text-[10px] uppercase tracking-[0.3em] font-medium mb-2">{offer.kicker}</p>
        <h3 className="font-heading text-2xl md:text-[26px] font-semibold text-[#111] leading-tight mb-3">
          {offer.title}
        </h3>
        <p className="text-[#666] text-sm font-light leading-relaxed mb-6">
          {offer.subtitle}
        </p>

        <div className="space-y-2.5">
          <button
            type="button"
            onClick={handleCrossOffer}
            disabled={submitting}
            data-testid="lead-magnet-cross-yes"
            className="w-full bg-[#C5A46D] text-[#0a0a0a] py-3.5 font-semibold text-sm tracking-wide hover:bg-[#D4AF37] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <><Loader2 size={16} className="animate-spin" /> Sending…</>
            ) : (
              <><Download size={15} /> {offer.cta}</>
            )}
          </button>
          <button
            type="button"
            onClick={handleSkip}
            disabled={submitting}
            data-testid="lead-magnet-cross-skip"
            className="w-full text-[#666] hover:text-[#111] py-2 font-medium text-sm transition-colors disabled:opacity-50"
          >
            No thanks, just the one I downloaded
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      data-testid="lead-magnet-modal"
      className="fixed inset-0 z-[120] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-[#0a0a0a]/80 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={() => !submitting && onClose()}
      />
      <div className="relative bg-white w-full max-w-md mx-auto shadow-2xl animate-in fade-in zoom-in-95 duration-300">
        <button
          onClick={() => !submitting && onClose()}
          data-testid="lead-magnet-close"
          className="absolute top-4 right-4 text-[#999] hover:text-[#111] transition-colors p-1 z-10"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="p-8 md:p-10">
          {phase === 'cross-offer' ? renderCrossOffer() : renderForm()}
        </div>
      </div>
    </div>
  );
};

export default LeadMagnetModal;
